import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DocumentTypeSelector } from "./DocumentTypeSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/context/AuthContext";

interface EditDocumentDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedDocument: any;
  updatedDocument: any;
  setUpdatedDocument: React.Dispatch<React.SetStateAction<any>>;

  handleUpdate: (e: React.FormEvent) => Promise<void>;
  saving: boolean;
}

export function EditDocumentDialog({
  isOpen,
  onOpenChange,
  selectedDocument,
  updatedDocument,
  setUpdatedDocument,
  handleUpdate,
  saving
}: EditDocumentDialogProps) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isSuperUser = profile?.isSuperUser;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common:documents.edit_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("common:documents.edit_dialog.description")}
          </DialogDescription>
        </DialogHeader>
        {selectedDocument && (
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit_document_type">
                  {t("common:documents.edit_dialog.document_type")}
                </Label>
                <DocumentTypeSelector
                  selectedTypeId={updatedDocument.documentTypeId}
                  searchValue={updatedDocument.documentTypeSearch || ""}
                  showDropdown={updatedDocument.showDocumentTypeDropdown}
                  className="edit-document-dropdown"
                  onChange={(value: string) => {
                    setUpdatedDocument((prev: any) => ({
                      ...prev,
                      documentTypeSearch: value,
                      // If user starts typing, we're in search mode
                      ...(value ? { documentTypeId: undefined } : {})
                    }));
                  }}
                  onSelect={(typeId: number, typeName: string) => {
                    setUpdatedDocument((prev: any) => ({
                      ...prev,
                      documentTypeId: typeId,
                      documentTypeSearch: typeName,
                      showDocumentTypeDropdown: false
                    }));
                  }}
                  onDropdownToggle={(isOpen: boolean) => {
                    setUpdatedDocument((prev: any) => ({
                      ...prev,
                      showDocumentTypeDropdown: isOpen
                    }));
                  }}
                  onClickInput={() => {
                    setUpdatedDocument((prev: any) => ({
                      ...prev,
                      showDocumentTypeDropdown: true
                    }));
                  }}
                />
              </div>
              {isSuperUser && (
                <div className="space-y-2">
                  <Label htmlFor="edit_document_status">
                    {t("common:documents.status", "Status")}
                  </Label>
                  <Select
                    value={
                      updatedDocument.documentStatus !== undefined &&
                      updatedDocument.documentStatus !== null
                        ? updatedDocument.documentStatus.toString()
                        : "in_review"
                    }
                    onValueChange={(value: string) =>
                      setUpdatedDocument((prev: any) => ({
                        ...prev,
                        documentStatus: value
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">
                        {t("common:documents.status_approved")}
                      </SelectItem>
                      <SelectItem value="in_review">
                        {t("common:documents.status_in_review")}
                      </SelectItem>
                      <SelectItem value="declined">
                        {t("common:documents.status_declined")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:documents.edit_dialog.cancel")}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("common:documents.edit_dialog.save")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
